import React, { useEffect, useState } from "react";
import { Lock, User, ShieldCheck, ArrowRight, CheckCircle2, AlertCircle, CircleDot } from "lucide-react";
import Dashboard from "./Dashboard";
import TiltCard from "../components/TiltCard";
import { pageBanners } from "../data/mock";
import PageBanner from "../components/PageBanner";
import BrandLogo from "../components/BrandLogo";
import BrandMarks from "../components/BrandMarks";
import { apiUrl, hasApi, SIGNIN_DISCONNECTED, SESSION_EXPIRED_MESSAGE } from "../lib/api";

const fmt = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e.msg || JSON.stringify(e)).join(" ") : d?.msg || "Sign in failed");

function friendlyError(text) {
  const raw = String(text || "");
  const lower = raw.toLowerCase();
  if (lower.includes("invalid username or password")) {
    return "That username or password is not recognised. Check Caps Lock and try again.";
  }
  if (lower.includes("too many failed")) {
    return "Too many failed attempts. Wait 15 minutes, or ask the administrator to help.";
  }
  if (lower.includes("no password")) {
    return "This account has no password yet. Ask the APMERCL administrator to set one.";
  }
  if (lower.includes("user not found") || lower.includes("session expired")) {
    return SESSION_EXPIRED_MESSAGE;
  }
  return raw || "Sign in failed.";
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | success | error
  const [shake, setShake] = useState(false);
  const [error, setError] = useState("");
  const [session, setSession] = useState(null);
  const [apiReady, setApiReady] = useState(hasApi() ? null : false);
  const [focusField, setFocusField] = useState("");

  useEffect(() => {
    if (!hasApi()) { setApiReady(false); return; }
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 8000);
    fetch(apiUrl("/health"), { signal: ctrl.signal })
      .then((r) => { if (!r.ok) throw new Error("down"); setApiReady(true); })
      .catch(() => setApiReady(false))
      .finally(() => clearTimeout(timer));
    return () => { clearTimeout(timer); ctrl.abort(); };
  }, []);

  useEffect(() => {
    const t = localStorage.getItem("apmercl_token");
    if (!t || !hasApi()) return;
    fetch(apiUrl("/api/auth/me"), { headers: { Authorization: `Bearer ${t}` } })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setSession({ user: d.user || d, portal_link: d.portal_link }))
      .catch(() => localStorage.removeItem("apmercl_token"));
  }, []);

  useEffect(() => {
    const onExpired = () => {
      localStorage.removeItem("apmercl_token");
      if (hasApi()) fetch(apiUrl("/api/auth/logout"), { method: "POST", credentials: "include" }).catch(() => {});
      setSession(null);
      setState("error");
      setError(SESSION_EXPIRED_MESSAGE);
    };
    window.addEventListener("apmercl:session-expired", onExpired);
    return () => window.removeEventListener("apmercl:session-expired", onExpired);
  }, []);

  const fail = (text) => { setError(friendlyError(text)); setShake(true); setState("error"); setTimeout(() => setShake(false), 600); };

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !pwd) return fail("Please enter both username and password.");
    if (!hasApi() || apiReady === false) return fail(SIGNIN_DISCONNECTED);
    setState("loading");
    try {
      const r = await fetch(apiUrl("/api/auth/login"), { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ username: email, password: pwd }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) return fail(fmt(d.detail) || "Sign in failed.");
      localStorage.setItem("apmercl_token", d.token);
      setState("success");
      setTimeout(() => setSession({ user: d.user, portal_link: d.portal_link }), 900);
    } catch {
      fail(SIGNIN_DISCONNECTED);
    }
  };

  const logout = () => { localStorage.removeItem("apmercl_token"); if (hasApi()) fetch(apiUrl("/api/auth/logout"), { method: "POST", credentials: "include" }).catch(() => {}); setSession(null); setState("idle"); setPwd(""); };

  if (session) return <Dashboard user={session.user} portalLink={session.portal_link} onLogout={logout} />;

  const disconnected = apiReady === false;
  const step = focusField === "password" || pwd ? 2 : 1;

  return (
    <div className="page-enter" style={{ minHeight: "78vh", padding: "0 0 60px", position: "relative" }}>
      <PageBanner image={pageBanners.login} title="Department Login" subtitle="Secure access for APMERCL personnel and college administrators." />
      <div style={{ position: "absolute", top: 220, left: "4%", opacity: 0.14 }} className="hide-mobile"><BrandLogo size={160} alt="" /></div>
      <div style={{ position: "absolute", bottom: 24, right: "5%", opacity: 0.12 }} className="hide-mobile"><BrandLogo size={120} alt="" /></div>

      <div style={{ maxWidth: 460, margin: "50px auto 0", position: "relative", padding: "0 20px" }}>
        <TiltCard intensity={3}>
          <div style={{ padding: 40, animation: shake ? "shake 0.4s" : "none" }} data-testid="login-card">
            <div style={{ textAlign: "center", marginBottom: 22 }}>
              <BrandMarks logoSize={72} plate />
              <h2 className="font-serif" style={{ fontSize: 26, color: "#1C1917", marginTop: 12, fontWeight: 800 }}>Sign in to your account</h2>
              <p style={{ color: "#78716C", fontSize: 13, marginTop: 6 }}>Use the official username given by the APMERCL administrator</p>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 16 }} data-testid="login-steps">
              <div className={`login-step ${step === 1 ? "login-step-on" : ""}`}><span className="login-step-dot">1</span> Username</div>
              <div className={`login-step ${step === 2 ? "login-step-on" : ""}`}><span className="login-step-dot">2</span> Password</div>
              <div className={`login-step ${state === "loading" || state === "success" ? "login-step-on" : ""}`}><span className="login-step-dot">3</span> Sign in</div>
            </div>

            {apiReady === null && (
              <div data-testid="login-checking" className="login-status login-status-wait">
                <span>Checking the sign-in service…</span>
                <CircleDot size={14} className="blink" />
              </div>
            )}
            {apiReady === true && (
              <div data-testid="login-api-ready" className="login-status login-status-ready">
                <span>Sign-in service is ready</span>
                <CheckCircle2 size={14} />
              </div>
            )}
            {disconnected && (
              <div data-testid="login-api-down" className="login-status login-status-down">
                <span style={{ display: "flex", gap: 8, textAlign: "left" }}>
                  <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                  {SIGNIN_DISCONNECTED}
                </span>
              </div>
            )}

            {state === "success" ? (
              <div data-testid="login-success" style={{ textAlign: "center", padding: "20px 0" }}>
                <div style={{ display: "inline-flex", padding: 18, borderRadius: "50%", background: "linear-gradient(180deg,#FACC15,#EA580C)", color: "#fff", marginBottom: 16, animation: "fadeUp 500ms ease-out" }}>
                  <CheckCircle2 size={36} />
                </div>
                <h3 className="font-serif" style={{ fontSize: 20, color: "#1C1917" }}>Signed in successfully</h3>
                <p style={{ color: "#78716C", fontSize: 13, marginTop: 6 }}>Opening your portal…</p>
              </div>
            ) : (
              <form onSubmit={submit}>
                <div style={{ marginBottom: 14, marginTop: 16 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#C2410C", display: "block", marginBottom: 6 }}>Username</label>
                  <div style={{ position: "relative" }}>
                    <User size={16} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: focusField === "username" ? "#EA580C" : "#78716C" }} />
                    <input
                      data-testid="login-email-input" type="text" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)}
                      onFocus={() => setFocusField("username")} onBlur={() => setFocusField("")}
                      className="input-3d" placeholder="e.g. principal.eluru" style={{ paddingLeft: 40 }}
                    />
                  </div>
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#C2410C", display: "block", marginBottom: 6 }}>Password</label>
                  <div style={{ position: "relative" }}>
                    <Lock size={16} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: focusField === "password" ? "#EA580C" : "#78716C" }} />
                    <input
                      data-testid="login-password-input" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
                      onFocus={() => setFocusField("password")} onBlur={() => setFocusField("")}
                      className="input-3d" placeholder="••••••••" style={{ paddingLeft: 40 }}
                    />
                  </div>
                </div>
                <button data-testid="login-submit-btn" type="submit" className="btn-primary-3d" style={{ width: "100%" }} disabled={state === "loading" || disconnected}>
                  {state === "loading" ? "Signing in..." : (<>Sign In <ArrowRight size={14} style={{ marginLeft: 6, verticalAlign: "middle" }} /></>)}
                </button>
                {state === "error" && (
                  <div data-testid="login-error" style={{ marginTop: 12, fontSize: 12.5, color: "#DC2626", textAlign: "center", fontWeight: 600 }}>{error}</div>
                )}
                <p data-testid="forgot-password-note" style={{ marginTop: 16, fontSize: 12.5, color: "#57534E", lineHeight: 1.55, textAlign: "center" }}>
                  Forgotten your password? Ask the APMERCL administrator to reset it. Self-service reset is not available.
                </p>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 16, padding: 12, background: "#FFF7ED", border: "1px solid rgba(249,115,22,0.25)", borderRadius: 10, fontSize: 12, color: "#C2410C", fontWeight: 600 }}>
                  <ShieldCheck size={14} /> Encrypted sign-in · For principals, department users &amp; APMERCL officers
                </div>
              </form>
            )}
          </div>
        </TiltCard>
      </div>

      <style>{`
        @keyframes shake {
          10%, 90% { transform: translateX(-2px); }
          20%, 80% { transform: translateX(3px); }
          30%, 50%, 70% { transform: translateX(-6px); }
          40%, 60% { transform: translateX(6px); }
        }
      `}</style>
    </div>
  );
}
